import type { Cell, Column, ColumnDef, Row, SortingState } from '@tanstack/react-table';

import { Button } from '@connected/ui/components/button';
import { Popout, PopoutBody, PopoutClose, PopoutContent, PopoutFooter, PopoutHeader, PopoutTitle, PopoutTrigger } from '@connected/ui/components/popout';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@connected/ui/components/table';
import { Link } from '@tanstack/react-router';
import { flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Paperclip } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import type { ObservationProps } from './observation';

import { Observation } from './observation';
import SearchInput from './search-input';

type DataTableProps<TData, TValue> = {
    columns: ColumnDef<TData, TValue>[];
    className?: string;
    data: TData[];
    pageSize?: number;
    cellProps?: (cell: Cell<TData, TValue>) => React.HTMLAttributes<HTMLTableCellElement> & { ref?: React.RefObject<HTMLTableCellElement | null> };
    rowProps?: (row: Row<TData>) => React.HTMLAttributes<HTMLTableRowElement> & { ref?: React.RefObject<HTMLTableRowElement | null> };
    expandedRow?: (row: Row<TData>) => React.ReactNode;
    whiteBgHeader?: boolean;
};

export function DataTable<TData, TValue>({
    className,
    columns,
    data,
    pageSize = 10,
    cellProps,
    rowProps,
    expandedRow,
    whiteBgHeader = false,
}: DataTableProps<TData, TValue>) {
    const { t } = useTranslation('general');
    const [sorting, setSorting] = useState<SortingState>([]);
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        state: {
            sorting,
        },
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize } },
    });

    // Calculate sticky offsets for columns (left and right)
    const stickyInfo = useMemo(() => {
        const info: Record<string, { offset: number; side: 'left' | 'right' }> = {};
        let leftOffset = 0;
        let rightOffset = 0;

        // Process left-sticky columns (from start)
        columns.forEach((column, idx) => {
            const columnId = (column as any).accessorKey || column.id || '';
            if ((column.meta as any)?.sticky && idx <= Math.floor(columns.length / 2)) {
                info[columnId] = { offset: leftOffset, side: 'left' };
                leftOffset += (column.meta as any)?.width || 150;
            }
        });

        // Process right-sticky columns (from end)
        for (let i = columns.length - 1; i >= 0; i--) {
            const column = columns[i];
            const columnId = (column as any).accessorKey || column.id || '';
            if ((column.meta as any)?.sticky && i > Math.floor(columns.length / 2)) {
                info[columnId] = { offset: rightOffset, side: 'right' };
                rightOffset += (column.meta as any)?.width || 150;
            }
        }

        return info;
    }, [columns]);

    return (
        <div className={cn('rounded-2xl border mb-10 overflow-hidden', className)}>
            <div className="overflow-x-auto">
                <Table className="min-w-full" key={table.getState().pagination.pageIndex}>
                    <TableHeader className={cn(
                        !whiteBgHeader ? 'bg-background-secondary' : 'bg-background-primary',
                    )}
                    >
                        {table.getHeaderGroups().map(headerGroup => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const isSticky = (header.column.columnDef.meta as any)?.sticky;
                                    const mobileHidden = (header.column.columnDef.meta as any)?.mobileHidden;
                                    const columnId = (header.column.columnDef as any).accessorKey || header.column.id || '';
                                    const stickyData = isSticky ? stickyInfo[columnId] : undefined;
                                    const isLeftSticky = stickyData?.side === 'left';
                                    const isRightSticky = stickyData?.side === 'right';
                                    return (
                                        <TableHead
                                            key={header.id}
                                            className={cn(
                                                isSticky && 'sticky z-10',
                                                isSticky && (!whiteBgHeader ? 'bg-background-secondary' : 'bg-background-primary'),
                                                isLeftSticky && 'after:absolute after:right-0 after:top-0 after:h-full after:w-px',
                                                isRightSticky && 'before:absolute before:left-0 before:top-0 before:h-full before:w-px',
                                                mobileHidden && 'max-sm:hidden',
                                            )}
                                            style={{
                                                minWidth: (header.column.columnDef.meta as any)?.width || undefined,
                                                width: (header.column.columnDef.meta as any)?.width || undefined,
                                                left: isLeftSticky ? stickyData.offset : undefined,
                                                right: isRightSticky ? stickyData.offset : undefined,
                                            }}
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext(),
                                                    )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length
                            ? (
                                    table.getRowModel().rows.map(row => (
                                        <Fragment key={row.id}>
                                            <TableRow
                                                key={row.id}
                                                data-state={row.getIsSelected() && 'selected'}
                                                {...rowProps?.(row)}
                                            >
                                                {row.getVisibleCells().map((cell) => {
                                                    const isSticky = (cell.column.columnDef.meta as any)?.sticky;
                                                    const mobileHidden = (cell.column.columnDef.meta as any)?.mobileHidden;
                                                    const columnId = (cell.column.columnDef as any).accessorKey || cell.column.id || '';
                                                    const stickyData = isSticky ? stickyInfo[columnId] : undefined;
                                                    const isLeftSticky = stickyData?.side === 'left';
                                                    const isRightSticky = stickyData?.side === 'right';
                                                    return (
                                                        <TableCell
                                                            key={cell.id}
                                                            {...cellProps?.(cell)}
                                                            className={cn(
                                                                cellProps?.(cell)?.className,
                                                                isSticky && 'sticky z-10 bg-background-primary',
                                                                isLeftSticky && 'after:absolute after:right-0 after:top-0 after:h-full after:w-px',
                                                                isRightSticky && 'before:absolute before:left-0 before:top-0 before:h-full before:w-px',
                                                                mobileHidden && 'max-sm:hidden',
                                                            )}
                                                            style={{
                                                                ...cellProps?.(cell)?.style,
                                                                minWidth: (cell.column.columnDef.meta as any)?.width || undefined,
                                                                width: (cell.column.columnDef.meta as any)?.width || undefined,
                                                                left: isLeftSticky ? stickyData.offset : undefined,
                                                                right: isRightSticky ? stickyData.offset : undefined,
                                                            }}
                                                        >
                                                            {flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext(),
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                            {expandedRow && expandedRow(row) && (
                                                <TableRow>
                                                    <TableCell colSpan={columns.length} className="p-0">
                                                        {expandedRow(row)}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </Fragment>

                                    ))
                                )
                            : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length}
                                            className="h-16 py-6 text-center"
                                        >
                                            {t('general.data-table.no-results')}
                                        </TableCell>
                                    </TableRow>
                                )}
                    </TableBody>
                </Table>
            </div>
            {table.getPageCount() > 1 && (
                <div className="pagination flex justify-between items-center p-4">
                    <button
                        type="button"
                        className="border px-3 rounded-lg h-10 w-fit text-foreground hover:no-underline hover:bg-background mt-2 md:mt-0 disabled:opacity-50"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {t('general.data-table.navigation.previous')}
                    </button>
                    <span>
                        {t('general.data-table.navigation.page')}
                        {' '}
                        <strong>
                            {table.getState().pagination.pageIndex + 1}
                            {' '}
                            of
                            {' '}
                            {table.getPageCount()}
                        </strong>
                    </span>
                    <button
                        type="button"
                        className="border px-3 rounded-lg h-10 w-fit text-foreground hover:no-underline hover:bg-background mt-2 md:mt-0 disabled:opacity-50"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        {t('general.data-table.navigation.next')}
                    </button>
                </div>
            )}
        </div>
    );
}

type DataTableHeaderButtonProps = {
    name: string;
    icon?: React.ReactNode;
    backgroundColor?: string;
    textColor?: string;
    callback: React.MouseEventHandler<HTMLButtonElement>;
    disabled?: boolean;
};

type DataTableHeaderProps = {
    title: string;
    subtitle?: string;
    label?: string;
    buttons?: DataTableHeaderButtonProps[];
    search?: any;
};

DataTable.TableHead = function DataTableHeader({
    title,
    subtitle,
    label,
    buttons,
    search,
}: DataTableHeaderProps) {
    return (
        <>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center border border-b-0 rounded-t-2xl p-4">
                <div className="flex flex-col md:flex-row items-start md:items-center">
                    <div className="flex flex-col p-2">
                        <h2 className="flex text-xl font-semibold">
                            {title}
                            {label && (
                                <p className="content-center bg-background-secondary text-text-primary ml-0 md:ml-2 mt-2 md:mt-0 p-1 text-xs rounded-lg border font-normal">
                                    {label}
                                </p>
                            )}
                        </h2>
                        <p className="mt-1 text-text-primary/70 text-base">
                            {subtitle}
                        </p>
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                    {search && (
                        <SearchInput route={search} />
                    )}
                    {buttons?.map(button => (
                        <div className="flex-col" key={button.name}>
                            <Button
                                key={button.name}
                                disabled={button.disabled}
                                variant="link"
                                className="border px-3 rounded-lg h-10 w-fit text-foreground hover:no-underline hover:bg-background mt-2 md:mt-0"
                                style={{
                                    ...(button?.backgroundColor && { backgroundColor: button.backgroundColor }),
                                    ...(button?.textColor && { color: button.textColor }),
                                }}
                                onClick={button.callback}
                            >
                                <div className="flex items-center">
                                    {button.icon && (
                                        <div className="w-5 max-w-5 h-5 max-h-5">
                                            {button.icon}
                                        </div>
                                    )}
                                    <p className="ml-2 mr-2 text-sm">
                                        {button.name}
                                    </p>
                                </div>
                            </Button>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

type DataTableHeaderCellProps
    = | {
        variant?: 'default';
        align?: 'start' | 'center' | 'end';
        children?: React.ReactNode;
        className?: string;
    }
    | {
        variant: 'sortDesc';
        column: Column<any, unknown>;
        align?: 'start' | 'center' | 'end';
        children?: React.ReactNode;
        className?: string;
    }
    | {
        variant: 'sort';
        default?: 'asc' | 'desc';
        column: Column<any, unknown>;
        align?: 'start' | 'center' | 'end';
        children?: React.ReactNode;
        className?: string;
    }
    | {
        variant: 'sortReverse';
        default?: 'asc' | 'desc';
        column: Column<any, unknown>;
        align?: 'start' | 'center' | 'end';
        children?: React.ReactNode;
        className?: string;
    };

DataTable.Header = function DataTableColumnHeader(
    props: DataTableHeaderCellProps,
) {
    switch (props.variant) {
        case 'sortDesc':
            return (
                <span
                    className={cn(
                        'text-text-primary/60 hover:text-text-primary/80 font-bold flex items-center select-none cursor-pointer',
                        props.className,
                        {
                            'ps-4': props.align === 'center',
                            'justify-start': props.align === 'start',
                            'justify-center': props.align === 'center',
                            'justify-end': props.align === 'end',
                        },
                    )}
                    onClick={() =>
                        props.column.toggleSorting(
                            props.column.getIsSorted() === 'desc',
                        )}
                >
                    {props.children}
                    <ChevronsUpDown className="ml-1 size-4" />
                </span>
            );
        case 'sortReverse':
            // eslint-disable-next-line react-hooks/rules-of-hooks -- HACK: Don't use useEffect here
            useEffect(() => {
                // initialize sorting (desc)
                if (props.column.getIsSorted() === false) {
                    props.column.toggleSorting(true);
                }
            }, [props.column]);

            return (
                <span
                    className={cn(
                        'text-text-primary/60 hover:text-text-primary/80 font-bold flex items-center select-none cursor-pointer',
                        props.className,
                        {
                            'ps-4': props.align === 'center',
                            'justify-start': props.align === 'start',
                            'justify-center': props.align === 'center',
                            'justify-end': props.align === 'end',
                        },
                    )}
                    onClick={() =>
                        props.column.toggleSorting(
                            props.column.getIsSorted() === 'asc',
                        )}
                >
                    {props.children}
                    {props.column.getIsSorted() === 'desc'
                        ? (
                                <ArrowDown className="ml-1 size-4" />
                            )
                        : (
                                <ArrowUp className="ml-1 size-4" />
                            )}
                </span>
            );
        case 'sort':
            // eslint-disable-next-line react-hooks/rules-of-hooks -- HACK: Don't use useEffect here
            useEffect(() => {
                // initialize sorting (asc)
                if (props.column.getIsSorted() === false) {
                    props.column.toggleSorting(
                        props.default && props.default === 'desc',
                    );
                }
            }, [props.column, props.default]);

            return (
                <span
                    className={cn(
                        'text-text-primary/60 hover:text-text-primary/80 font-bold flex items-center select-none cursor-pointer',
                        props.className,
                        {
                            'ps-4': props.align === 'center',
                            'justify-start': props.align === 'start',
                            'justify-center': props.align === 'center',
                            'justify-end': props.align === 'end',
                        },
                    )}
                    onClick={() =>
                        props.column.toggleSorting(
                            props.column.getIsSorted() === 'asc',
                        )}
                >
                    {props.children}
                    {props.column.getIsSorted() === 'asc'
                        ? (
                                <ArrowDown className="ml-1 size-4" />
                            )
                        : (
                                <ArrowUp className="ml-1 size-4" />
                            )}
                </span>
            );
        default:
            return (
                <span
                    className={cn(
                        'text-text-primary/60 font-bold flex items-center',
                        props.className,
                        {
                            'justify-start': props.align === 'start',
                            'justify-center': props.align === 'center',
                            'justify-end': props.align === 'end',
                        },
                    )}
                >
                    {props.children}
                </span>
            );
    }
};

type DataTableCellProps
    = | {
        variant?: 'default' | 'bold' | 'medium';
        align?: 'start' | 'center' | 'end';
        children?: React.ReactNode;
    }
    | {
        variant: 'observation';
        props: ObservationProps;
        label?: string;
    }
    | {
        variant: 'link';
        to?: string;
        params?: any;
        align?: 'start' | 'center' | 'end';
        children?: React.ReactNode;
    }
    | {
        variant: 'colorLabel';
        color: string;
        align?: 'start' | 'center' | 'end';
        children?: React.ReactNode;
    }
    | {
        variant: 'fileIcon';
        fileType: string;
    }
    | {
        variant: 'files';
        files: object[];
        align?: 'start' | 'center' | 'end';
    };

DataTable.Cell = function DataTableCell(props: DataTableCellProps) {
    // Dynamic import of all file type icons
    const fileTypeIcons = import.meta.glob('@/assets/file-types/*.png', { eager: true, import: 'default' });

    switch (props.variant) {
        case 'observation':
            return (
                <Observation
                    {...props.props}
                    className="text-center"
                    label={props.label || props.props.label}
                />
            );
        case 'link':
            return (
                <Link
                    className="cursor-pointer"
                    to={props.to}
                    params={props.params || {}}
                >
                    <div className="font-bold text-primary">
                        {props.children}
                    </div>
                </Link>
            );
        case 'bold':
            return (
                <span
                    className={cn(
                        'text-text-primary/80 font-semibold flex items-center',
                        {
                            'justify-start': props.align === 'start',
                            'justify-center': props.align === 'center',
                            'justify-end': props.align === 'end',
                        },
                    )}
                >
                    {props.children}
                </span>
            );

        case 'medium':
            return (
                <span
                    className={cn(
                        'text-text-primary/80 font-medium flex items-center',
                        {
                            'justify-start': props.align === 'start',
                            'justify-center': props.align === 'center',
                            'justify-end': props.align === 'end',
                        },
                    )}
                >
                    {props.children}
                </span>
            );
        case 'colorLabel':
            return (
                <div
                    className={cn(
                        'flex items-center',
                        {
                            'justify-start': props.align === 'start',
                            'justify-center': props.align === 'center',
                            'justify-end': props.align === 'end',
                        },
                    )}
                >
                    <p className="text-text-primary/80 font-semibold border rounded-md px-2 py-1 w-fit flex items-center gap-2">
                        <div
                            className="rounded-full h-2 w-2"
                            style={{ backgroundColor: props.color }}
                        >
                        </div>
                        <div>{props.children}</div>
                    </p>
                </div>
            );
        case 'fileIcon':
            return (
                <img
                    src={String(fileTypeIcons[`/src/assets/file-types/${props.fileType.toLowerCase()}.png`] || fileTypeIcons[`/src/assets/file-types/unknown.png`])}
                    className="h-[2.5rem] w-[2.5rem] object-cover rounded-xs me-2"
                    alt=""
                />
            );
        case 'files':
            return (
                <Popout>
                    <PopoutTrigger
                        className="font-semibold hover:no-underline focus:outline-none text-text-primary/60 p-2 h-full flex items-center justify-center"
                        asChild
                    >
                        <Button variant="link">
                            <Paperclip className="w-4 h-4" />
                        </Button>
                    </PopoutTrigger>
                    <PopoutContent>
                        <PopoutHeader>
                            <PopoutTitle>TODO:</PopoutTitle>
                        </PopoutHeader>
                        <PopoutBody>
                            <div>TODO: </div>
                        </PopoutBody>
                        <PopoutFooter>
                            <PopoutClose asChild>
                                <Button variant="ghost">TODO:</Button>
                            </PopoutClose>
                            <PopoutClose asChild>TODO:</PopoutClose>
                        </PopoutFooter>
                    </PopoutContent>
                </Popout>
            );
        default:
            return (
                <span
                    className={cn(
                        'text-text-primary/60 flex items-center',
                        {
                            'justify-start': props.align === 'start',
                            'justify-center': props.align === 'center',
                            'justify-end': props.align === 'end',
                        },
                    )}
                >
                    {props.children}
                </span>
            );
    }
};
